import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultThrowOnError } from "@/queries/errors";
import { useOrganization } from "@/queries/organization-queries";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { TimeRange } from "@/routes/insights/insights";
import { type Timestamp } from "@bufbuild/protobuf";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Resolution } from "gitpod-next-api/gitpod/v1/usage_pb";

export const insightsQueryKey = {
    activeUsersCount: () => keyWithPrincipal(["insights", "activeUsersCount"]),
    mostActiveUsers: () => keyWithPrincipal(["insights", "mostActiveUsers"]),
    topEnvironmentClasses: () => keyWithPrincipal(["insights", "topEnvironmentClasses"]),
    topProjects: () => keyWithPrincipal(["insights", "topProjects"]),
    activeUsersTimeSeries: () => keyWithPrincipal(["insights", "activeUsersTimeSeries"]),
    activeEnvironmentsCount: () => keyWithPrincipal(["insights", "activeEnvironmentsCount"]),
    activeEnvironmentsTimeSeries: () => keyWithPrincipal(["insights", "activeEnvironmentsTimeSeries"]),
    environmentRuntime: () => keyWithPrincipal(["insights", "environmentRuntime"]),
    environmentRuntimeTimeSeries: () => keyWithPrincipal(["insights", "environmentRuntimeTimeSeries"]),
};

export const useMostActiveUsers = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    return useInfiniteQuery({
        queryKey: [...insightsQueryKey.mostActiveUsers(), { selectedRange: selectedRange.days, projectId }],
        queryFn: ({ pageParam = "" }) => {
            return api.usageService.getTopActiveUsers({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                projectId,
                pagination: {
                    token: pageParam,
                },
            });
        },
        getNextPageParam: (lastPage) => lastPage.pagination?.nextToken || undefined,
        initialPageParam: "",
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });
};

export const useTopProjects = (startTimestamp: Timestamp, endTimestamp: Timestamp, selectedRange: TimeRange) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useInfiniteQuery({
        queryKey: [...insightsQueryKey.topProjects(), { selectedRange: selectedRange.days }],
        queryFn: ({ pageParam = "" }) => {
            return api.usageService.getTopProjects({
                dateRange: { startTime: startTimestamp, endTime: endTimestamp },
                pagination: {
                    token: pageParam,
                },
            });
        },
        getNextPageParam: (lastPage) => lastPage.pagination?.nextToken || undefined,
        initialPageParam: "",
        throwOnError: defaultThrowOnError,
        enabled: !!user,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });
};

export const useTopEnvironmentClasses = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    const { data, isLoading, error } = useInfiniteQuery({
        queryKey: [...insightsQueryKey.topEnvironmentClasses(), { selectedRange: selectedRange.days, projectId }],
        queryFn: ({ pageParam = "" }) => {
            return api.usageService.getTopEnvironmentClasses({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                projectId,
                pagination: {
                    token: pageParam,
                },
            });
        },
        getNextPageParam: (lastPage) => lastPage.pagination?.nextToken || undefined,
        initialPageParam: "",
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });

    return { data, isLoading, error };
};

export const useActiveUsersCount = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    const { data, isLoading, error } = useQuery({
        queryKey: [...insightsQueryKey.activeUsersCount(), { selectedRange: selectedRange.days, projectId }],
        queryFn: () => {
            return api.usageService.getActiveUsersCount({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                projectId,
            });
        },
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });

    return { data, isLoading, error };
};

export const useActiveEnvironmentsCount = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    const { data, isLoading, error } = useQuery({
        queryKey: [...insightsQueryKey.activeEnvironmentsCount(), { selectedRange: selectedRange.days, projectId }],
        queryFn: () => {
            return api.usageService.getActiveEnvironmentsCount({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                projectId,
            });
        },
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60,
        gcTime: 1_000 * 60 * 60,
    });

    return { data, isLoading, error };
};

export const useActiveUsersTimeSeries = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    const { data, isLoading, error } = useQuery({
        queryKey: [...insightsQueryKey.activeUsersTimeSeries(), { selectedRange: selectedRange.days, projectId }],
        queryFn: () => {
            return api.usageService.getActiveUsersTimeSeries({
                dateRange: {
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                },
                projectId,
                resolution: calculateResolution(selectedRange.days, Resolution.DAILY),
            });
        },
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60 * 12,
        gcTime: 1_000 * 60 * 60 * 24,
    });

    return { data, isLoading, error };
};

export const useEnvironmentRuntime = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();

    const { data, isLoading, error } = useQuery({
        queryKey: [...insightsQueryKey.environmentRuntime(), { selectedRange: selectedRange.days, projectId }],
        queryFn: () => {
            return api.usageService.getEnvironmentRuntime({
                dateRange: { startTime: startTimestamp, endTime: endTimestamp },
                projectId,
            });
        },
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60 * 12,
        gcTime: 1_000 * 60 * 60 * 24,
    });

    return { data, isLoading, error };
};

export const useActiveEnvironmentsTimeSeries = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();
    const resolution = calculateResolution(selectedRange.days, Resolution.HOURLY);

    const { data, isLoading, error } = useQuery({
        queryKey: [
            ...insightsQueryKey.activeEnvironmentsTimeSeries(),
            { selectedRange: selectedRange.days, projectId },
        ],
        queryFn: () => {
            return api.usageService.getActiveEnvironmentsTimeSeries({
                dateRange: { startTime: startTimestamp, endTime: endTimestamp },
                projectId,
                resolution,
            });
        },
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60 * 12,
        gcTime: 1_000 * 60 * 60 * 24,
    });

    return { data, resolution, isLoading, error };
};

export const useEnvironmentRuntimeTimeSeries = (
    startTimestamp: Timestamp,
    endTimestamp: Timestamp,
    selectedRange: TimeRange,
    projectId?: string,
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();
    const resolution = calculateResolution(selectedRange.days, Resolution.HOURLY);

    const { data, isLoading, error } = useQuery({
        queryKey: [
            ...insightsQueryKey.environmentRuntimeTimeSeries(),
            { selectedRange: selectedRange.days, projectId },
        ],
        queryFn: () => {
            return api.usageService.getEnvironmentRuntimeTimeSeries({
                dateRange: { startTime: startTimestamp, endTime: endTimestamp },
                projectId,
                resolution,
            });
        },
        throwOnError: defaultThrowOnError,
        enabled: !!user && !!organization,
        staleTime: 1_000 * 60 * 60 * 12,
        gcTime: 1_000 * 60 * 60 * 24,
    });

    return { data, resolution, isLoading, error };
};

/**
 * @returns the appropriate resolution for the given date range.
 */
const calculateResolution = (days: number, minimumResolution: Resolution = Resolution.HOURLY): Resolution => {
    const weeks = days / 7;
    if (weeks > 100) {
        return Resolution.MONTHLY;
    }
    if (days > 100) {
        return Resolution.WEEKLY;
    }
    if (days * 24 > 100) {
        return Resolution.DAILY;
    }

    return minimumResolution;
};
